import { lazy } from 'react';

import { FederatedModule } from '@zenbusiness/application-commons-federation';
import { Route, Outlet } from 'react-router-dom';

import Error from '#client/components/ErrorBoundary/Error';
import Loading from '#client/components/LoadingV2';
import { FEDERATED, ROUTES } from '#client/manifest';
import SmartNavigate from '#client/routes/SmartNavigate';
import timeout from '#client/utils/timeout';

const DomainContactMutate = lazy(() => timeout(import(/* webpackChunkName: 'domains' */'#client/pages/DomainContactMutate')));
const DomainDnsCreate = lazy(() => timeout(import(/* webpackChunkName: 'domains' */'#client/pages/DomainDnsCreate')));
const DomainDnsMutate = lazy(() => timeout(import(/* webpackChunkName: 'domains' */'#client/pages/DomainDnsMutate')));
const DomainList = lazy(() => timeout(import(/* webpackChunkName: 'domains' */'#client/pages/DomainList')));
const DomainNameserverMutate = lazy(() => timeout(import(/* webpackChunkName: 'domains' */'#client/pages/DomainNameserverMutate')));
const DomainPrivacy = lazy(() => timeout(import(/* webpackChunkName: 'domains' */'#client/pages/DomainPrivacy')));
const DomainView = lazy(() => timeout(import(/* webpackChunkName: 'domains' */'#client/pages/DomainView')));
const ExternalDomainCreate = lazy(() => timeout(import(/* webpackChunkName: 'domains' */'#client/pages/ExternalDomainCreate')));
const ExternalDomainRecords = lazy(() => timeout(import(/* webpackChunkName: 'domains' */'#client/pages/ExternalDomainRecords')));
const ExternalDomainVerify = lazy(() => timeout(import(/* webpackChunkName: 'domains' */'#client/pages/ExternalDomainVerify')));
const LogoBuilder = lazy(() => timeout(import(/* webpackChunkName: 'logoBuilder' */'#client/pages/LogoBuilder')));
const MarketingTools = lazy(() => timeout(import(/* webpackChunkName: 'MarketingTools' */'#client/pages/MarketingTools')));
const WebsiteList = lazy(() => timeout(import(/* webpackChunkName: 'websites' */'#client/pages/WebsiteList')));

const fedProps = {
  loadingElement: <Loading />,
  errorElement: <Error />
};

const bucket1Routes = (bucket1Enabled) => {
  if (bucket1Enabled) {
    return (
      <>
        <Route path="external-domains" element={<FederatedModule {...fedProps} {...FEDERATED.WebsitesUi} exportName="ExternalDomainCreate" />} />
        <Route path="logo-builder/coming-soon" element={<FederatedModule {...fedProps} {...FEDERATED.WebsitesUi} exportName="LogoBuilder" />} />
      </>
    );
  }

  return (
    <>
      <Route path="external-domains" element={<ExternalDomainCreate />} />
      <Route path="logo-builder/coming-soon" element={<LogoBuilder />} />
    </>
  );
};

const bucket2Routes = (bucket2Enabled) => {
  if (bucket2Enabled) {
    return (
      <>
        {/* .../marketing-tools/domains/* */}
        <Route path="domains" element={<Outlet />}>
          <Route
            index
            element={<FederatedModule {...fedProps} {...FEDERATED.WebsitesUi} exportName="DomainList" />}
          />

          <Route path=":domainUuid" element={<Outlet />}>
            <Route index element={<FederatedModule {...fedProps} {...FEDERATED.WebsitesUi} exportName="DomainView" />} />
            <Route
              path="contacts/:domainContactUuid"
              element={<FederatedModule {...fedProps} {...FEDERATED.WebsitesUi} exportName="DomainContactMutate" />}
            />
            <Route
              path="dns"
              element={<FederatedModule {...fedProps} {...FEDERATED.WebsitesUi} exportName="DomainDnsCreate" />}
            />
            <Route
              path="dns/:domainDnsUuid"
              element={<FederatedModule {...fedProps} {...FEDERATED.WebsitesUi} exportName="DomainDnsMutate" />}
            />
            <Route
              path="external-records"
              element={<FederatedModule {...fedProps} {...FEDERATED.WebsitesUi} exportName="ExternalDomainRecords" />}
            />
            <Route
              path="external-verify"
              element={<FederatedModule {...fedProps} {...FEDERATED.WebsitesUi} exportName="ExternalDomainVerify" />}
            />
            <Route
              path="nameservers"
              element={<FederatedModule {...fedProps} {...FEDERATED.WebsitesUi} exportName="DomainNameserverMutate" />}
            />
            <Route
              path="privacy"
              element={<FederatedModule {...fedProps} {...FEDERATED.WebsitesUi} exportName="DomainPrivacy" />}
            />
            <Route path="*" element={<SmartNavigate replace to={ROUTES.domain} />} />
          </Route>

          <Route path="*" element={<SmartNavigate replace to={ROUTES.domainList} />} />
        </Route>
      </>
    );
  }

  return (
    <>
      {/* .../marketing-tools/domains/* */}
      <Route path="domains" element={<Outlet />}>
        <Route index element={<DomainList />} />

        <Route path=":domainUuid" element={<Outlet />}>
          <Route index element={<DomainView />} />
          <Route path="contacts/:domainContactUuid" element={<DomainContactMutate />} />
          <Route path="dns" element={<DomainDnsCreate />} />
          <Route path="dns/:domainDnsUuid" element={<DomainDnsMutate />} />
          <Route path="external-records" element={<ExternalDomainRecords />} />
          <Route path="external-verify" element={<ExternalDomainVerify />} />
          <Route path="nameservers" element={<DomainNameserverMutate />} />
          <Route path="privacy" element={<DomainPrivacy />} />

          <Route path="*" element={<SmartNavigate replace to={ROUTES.domain} />} />
        </Route>

        <Route path="*" element={<SmartNavigate replace to={ROUTES.domainList} />} />
      </Route>
    </>
  );
};

const bucket3Routes = (bucket3Enabled) => {
  if (bucket3Enabled) {
    return (
      <>
        <Route index element={<FederatedModule {...fedProps} {...FEDERATED.WebsitesUi} exportName="MarketingTools" />} />
        <Route path="websites" element={<FederatedModule {...fedProps} {...FEDERATED.WebsitesUi} exportName="WebsiteList" />} />
      </>
    );
  }

  return (
    <>
      <Route index element={<MarketingTools />} />
      <Route path="websites" element={<WebsiteList />} />
    </>
  );
};

function websitesRoutes(enableWebsitesUIMFE = 0) {
  const bucket1Enabled = enableWebsitesUIMFE >= 1;
  const bucket2Enabled = enableWebsitesUIMFE >= 2;
  const bucket3Enabled = enableWebsitesUIMFE >= 3;

  return (
    <>
      { bucket1Routes(bucket1Enabled) }
      { bucket2Routes(bucket2Enabled) }
      { bucket3Routes(bucket3Enabled) }
    </>
  );
}

export default websitesRoutes;
